/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import { pluck, preDefStr, POSITION_RIGHT, convertColor,
  pluckNumber, getFirstValue, getMouseCoordinate, BLANKSTRING, POSITION_LEFT, POSITION_TOP, pluckFontSizeMaintainUnit } from '@fusioncharts/core/src/lib';
import MSCartesian from '@fusioncharts/charts/src/chart/_internal/mscartesian';
import virtualCanvasFactory from '@fusioncharts/charts/src/factories/vcanvas';
import datasetFactory from '../../factories/candlestick-dataset';
import axisFactory from '../../factories/candlestick-axis';
import { CandleStickDataset, CandleStickBarDataset, CandleStickLineDataset } from '../../dataset/candlestick';
import isEqualString from '@fusioncharts/utils/src/string/equal';

const MOUSEOUT = 'fc-mouseout',
  CANDLESTICK = 'candlestick',
  padding = 6;
let altHGridColorStr = preDefStr.altHGridColorStr,
  altHGridAlphaStr = preDefStr.altHGridAlphaStr,
  math = Math,
  mathMax = math.max,
  mathRound = math.round,
  POSITION_BOTTOM = preDefStr.POSITION_BOTTOM,
  divLineAlpha3DStr = preDefStr.divLineAlpha3DStr,
  defaultFontStr = preDefStr.defaultFontStr,
  divLineAlphaStr = preDefStr.divLineAlphaStr,
  altVGridColorStr = preDefStr.altVGridColorStr,
  altVGridAlphaStr = preDefStr.altVGridAlphaStr,
  colorStrings = preDefStr.colors,
  COLOR_000000 = colorStrings.c000000,
  chartPaletteStr = {
    chart2D: {
      bgColor: 'bgColor',
      bgAlpha: 'bgAlpha',
      bgAngle: 'bgAngle',
      bgRatio: 'bgRatio',
      canvasBgColor: 'canvasBgColor',
      canvasBaseColor: 'canvasBaseColor',
      divLineColor: 'divLineColor',
      legendBgColor: 'legendBgColor',
      legendBorderColor: 'legendBorderColor',
      toolTipbgColor: 'toolTipbgColor',
      toolTipBorderColor: 'toolTipBorderColor',
      baseFontColor: 'baseFontColor',
      anchorBgColor: 'anchorBgColor'
    },
    chart3D: {
      bgColor: 'bgColor3D',
      bgAlpha: 'bgAlpha3D',
      bgAngle: 'bgAngle3D',
      bgRatio: 'bgRatio3D',
      canvasBgColor: 'canvasBgColor3D',
      canvasBaseColor: 'canvasBaseColor3D',
      divLineColor: 'divLineColor3D',
      divLineAlpha: divLineAlpha3DStr,
      legendBgColor: 'legendBgColor3D',
      legendBorderColor: 'legendBorderColor3D',
      toolTipbgColor: 'toolTipbgColor3D',
      toolTipBorderColor: 'toolTipBorderColor3D',
      baseFontColor: 'baseFontColor3D',
      anchorBgColor: 'anchorBgColor3D'
    }
  },
  isVolumeChartRequired = function (dataObj) {
    if (!(dataObj && dataObj.dataset)) {
      return 0;
    }
    let data = dataObj.dataset[0].data;
    // eslint-disable-next-line good-practices/no-function-dependency
    return !!((Array.isArray(data) && data.filter(v => v.volume)) || []).length;
  };
/**
 * class definition for candlestick chart API
 */
class CandleStick extends MSCartesian {
  /**
   * constructor function of this class
   */
  constructor () {
    super();
    this.isDual = true;
    this.paletteIndex = 3;
    this.hasInteractiveLegend = false;
    this.numOfCanvas = 2;
    this.registerFactory('vCanvas', virtualCanvasFactory, ['axis']);
    this.registerFactory('axis', axisFactory, ['canvas']);
    this.registerFactory('dataset', datasetFactory, ['vCanvas']);
  }
  /**
   * Returns the name of the component
   *
   * @return {string} The name of the component
   */
  getName () {
    return 'candlestick';
  }
  /**
   * Returns the name of the component
   *
   * @return {string} The name of the component
   */
  static getName () {
    return 'candlestick';
  }
  /**
   * This method return the dataset definations for this charts
   * @param {string} name   name of the dataset that is required
   * @return {Object}       Required dataset class definition
   */
  getDSdef (name) {
    return name === 'bar' ? CandleStickBarDataset : ((name === 'line') ? CandleStickLineDataset : CandleStickDataset);
  }
  /**
   * parse defualt configuration of the chart
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.hasLegend = true;
    config.defaultDatasetType = CANDLESTICK;
    config.drawanchors = 0;
    config.enablemousetracking = true;
    config.iscandlestick = true;
    config.canvasborderthickness = 1;
  }
  /**
   * function to create component and configure and also configure the chart
   * It creates components which are depend on data and configure them and also configure chart
   * It calls helper function namely, configure
   * @param  {Object} dataObj contains json data of the chart
   */
  configureAttributes (dataObj) {
    super.configureAttributes(dataObj);
  }
  /**
   * Parse the chart attributes and store in chart's config
   * @param {Object} dataObj User input json
   */
  parseChartAttr (dataObj) {
    super.parseChartAttr(dataObj);
    let iapi = this,
      volumeHeightPercent,
      config = iapi.config,
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      colorM = iapi.getFromEnv('color-manager');
    config.showVolumeChart = pluckNumber(chartAttrs.showvolumechart, isVolumeChartRequired(dataObj), 1);
    config.rollOverBandColor = convertColor(
      pluck(chartAttrs.rolloverbandcolor, colorM.getColor(altHGridColorStr)),
      pluck(chartAttrs.rolloverbandalpha, colorM.getColor(altHGridAlphaStr)));

    config.crosslinecolor = pluck(chartAttrs.crosslinecolor,
      chartAttrs.rolloverbandcolor, colorM.getColor(altHGridColorStr));
    config.drawcrosslineontop = 0;
    config.crosslinealpha = pluckNumber(chartAttrs.crosslinealpha,
      chartAttrs.rolloverbandalpha, colorM.getColor(altHGridAlphaStr));
    config.drawcrossline = 1;
    config.skipClipping = true;
    chartAttrs = iapi.getFromEnv('chart-attrib');
    config.vplotbordercolor = getFirstValue(chartAttrs.vplotbordercolor, BLANKSTRING);
    config.vplotborderalpha = getFirstValue(chartAttrs.vplotborderalpha, BLANKSTRING);
    config.vplotborderthickness = pluckNumber(chartAttrs.vplotborderthickness, 1);
    config.showplotborder = pluckNumber(chartAttrs.showvplotborder, 1);
    volumeHeightPercent = pluckNumber(chartAttrs.volumeheightpercent, 40);
    config.volumeHeightPercent = volumeHeightPercent < 20 ? 20 : (volumeHeightPercent > 80 ? 80
      : volumeHeightPercent);
    config.canvasBorderWidth = pluckNumber(chartAttrs.canvasborderthickness, 1);
  }

  /**
   * calculate and allote space for various components
   * every components are get notified with maximum space and then components return
   * the available space.
   */
  _spaceManager () {
    var spaceForActionBar,
      actionBarSpace,
      availableWidth,
      availableHeight,
      iapi = this,
      config = iapi.config,
      components = iapi.getChildren(),
      legendPosition = config.legendPosition ? config.legendPosition.split('-') : [],
      xAxis = components.xAxis && components.xAxis[0],
      yAxis = components.yAxis && components.yAxis[0], // eslint-disable-line good-practices/no-single-usage-variable
      yAxis2 = components.yAxis && components.yAxis[1], // eslint-disable-line good-practices/no-single-usage-variable
      legend = iapi.getFromEnv('legend'), // eslint-disable-line good-practices/no-single-usage-variable
      canvasHeight,
      xAxisDimensions,
      showVolumeChart = iapi.config.showVolumeChart,
      volumeHeightPercent = showVolumeChart ? config.volumeHeightPercent : 0,
      canvas = components.canvas[0],
      canvasConfig = canvas.config,
      volumeCanvas,
      volumeCanvasConfig,
      volumeCanvasY,
      yAxisDim,
      vYAxisDim,
      width = config.width,
      height = config.height,
      actualYDim = {},
      allottedSpace,
      chartBorderWidth = config.chartBorderWidth,
      canvasBorderWidth = canvasConfig.canvasBorderWidth,
      minCanvasHeight = config.minCanvasHeight,
      minCanvasWidth = config.minCanvasWidth,
      canvasMarginLeft = config.canvasMarginLeft,
      canvasMarginRight = config.canvasMarginRight,
      canvasMarginTop = config.canvasMarginTop,
      canvasMarginBottom = config.canvasMarginBottom,
      diff,
      currentCanvasHeight,
      currentCanvasWidth,
      heightAdjust,
      origCanvasTopMargin = config.origCanvasTopMargin,
      origCanvasBottomMargin = config.origCanvasBottomMargin,
      origCanvasLeftMargin = config.origCanvasLeftMargin,
      origCanvasRightMargin = config.origCanvasRightMargin,
      left,
      right,
      top,
      bottom,
      widthAdjust,
      sum;
      // Manage space

    iapi._allocateSpace({
      top: chartBorderWidth,
      bottom: chartBorderWidth,
      left: chartBorderWidth,
      right: chartBorderWidth
    });

    // iapi._allocateSpace ( {
    //     left : config.canvasMarginLeft,
    //     right : config.canvasMarginRight
    // });

    spaceForActionBar = config.availableHeight * 0.225; // eslint-disable-line good-practices/no-single-usage-variable
    actionBarSpace = (iapi._manageActionBarSpace && iapi._manageActionBarSpace(spaceForActionBar)) || {}; // eslint-disable-line good-practices/no-single-usage-variable
    iapi._allocateSpace(actionBarSpace);

    if (legendPosition[0] === POSITION_LEFT || legendPosition[0] === POSITION_RIGHT) {
      allottedSpace = config.canvasWidth * 0.225;
    } else {
      allottedSpace = config.canvasHeight * 0.3;
    }
    ((config.hasLegend !== false) && xAxis) && config.showLegend &&
              iapi._allocateSpace(legend._manageLegendPosition(allottedSpace));
    availableWidth = config.canvasWidth * 0.7;
    yAxisDim = yAxis.placeAxis(availableWidth);
    vYAxisDim = showVolumeChart ? yAxis2.placeAxis(availableWidth) : {};
    actualYDim.left = mathMax(yAxisDim.left, vYAxisDim.left || 0);
    actualYDim.right = mathMax(yAxisDim.right, vYAxisDim.right || 0);
    iapi._allocateSpace(actualYDim);

    // Check for minimun canvas width for applying canvas left and right margin.
    if (minCanvasWidth > width - canvasMarginLeft - canvasMarginRight) {
      widthAdjust = true;
      diff = config.canvasWidth - minCanvasWidth;
      sum = canvasMarginLeft + canvasMarginRight;
      canvasMarginLeft = config.canvasMarginLeft = diff * canvasMarginLeft / sum;
      canvasMarginRight = config.canvasMarginRight = diff * canvasMarginRight / sum;
    }

    // Calculating the left and right canvas margin.
    left = canvasMarginLeft > config.canvasLeft ? (canvasMarginLeft - config.canvasLeft) : 0;
    right = canvasMarginRight > (width - config.canvasRight) ? (canvasMarginRight +
              config.canvasRight - width) : 0;

    iapi._allocateSpace({
      left: left,
      right: right
    });

    // Forcing canvas width to its minimum
    if (widthAdjust) {
      sum = origCanvasLeftMargin + origCanvasRightMargin;
      currentCanvasWidth = config.canvasWidth;
      if (currentCanvasWidth > minCanvasWidth) {
        diff = currentCanvasWidth - minCanvasWidth;
        left = diff * origCanvasLeftMargin / sum;
        right = diff * origCanvasRightMargin / sum;
      }
      iapi._allocateSpace({
        left: left,
        right: right
      });
    }

    availableHeight = config.canvasHeight * 0.225;

    availableHeight = (legendPosition[0] === POSITION_TOP || legendPosition[0] === POSITION_BOTTOM) ? config.canvasHeight * 0.6
      : config.canvasWidth * 0.6;
    // a space manager that manages the space for the tools as well as the captions.
    iapi._manageChartMenuBar(availableHeight);

    iapi._allocateSpace({
      top: config.canvasMarginTop,
      bottom: config.canvasMarginBottom
    });

    availableHeight = config.canvasHeight * 0.3;
    xAxisDimensions = xAxis.placeAxis(availableHeight);
    xAxis && iapi._allocateSpace(xAxisDimensions);
    xAxisDimensions.bottom += padding;
    canvasConfig.intermediarySpace = xAxisDimensions.bottom;

    // enforcing the 10 pixel deduction in canvas height to set a padding between canvas and legend
    if (showVolumeChart) {
      iapi._allocateSpace({ bottom: 10 });
    }
    iapi._allocateSpace({
      top: canvasBorderWidth,
      bottom: canvasBorderWidth * 2,
      left: canvasBorderWidth,
      right: canvasBorderWidth
    });

    // Check for minimum canvas height for applying top and bottom margin.
    if (minCanvasHeight > height - canvasMarginTop - canvasMarginBottom) {
      heightAdjust = true;
      diff = config.canvasHeight - minCanvasHeight;
      sum = canvasMarginTop + canvasMarginBottom;
      canvasMarginTop = config.canvasMarginTop = diff * canvasMarginTop / sum;
      canvasMarginBottom = config.canvasMarginBottom = diff * canvasMarginBottom / sum;
    }

    // Allocate space for canvas margin only if the margin is less than the margin entered by the user.
    top = canvasMarginTop > config.canvasTop ? (canvasMarginTop - config.canvasTop) : 0;
    bottom = canvasMarginBottom > (height - config.canvasBottom) ? (canvasMarginBottom +
              config.canvasBottom - height) : 0;

    iapi._allocateSpace({
      top: top,
      bottom: bottom
    });

    // Forcing canvas height to its minimum
    if (heightAdjust) {
      sum = origCanvasTopMargin + origCanvasBottomMargin;
      currentCanvasHeight = config.canvasHeight;
      if (currentCanvasHeight > minCanvasHeight) {
        diff = currentCanvasHeight - minCanvasHeight;
        top = diff * origCanvasTopMargin / sum;
        bottom = diff * origCanvasBottomMargin / sum;
      }
      iapi._allocateSpace({
        top: top,
        bottom: bottom
      });
    }

    canvasHeight = config.canvasHeight;
    canvasConfig.canvasHeight = mathRound((100 - volumeHeightPercent) / 100 * canvasHeight);

    canvasConfig.canvasTop = config.canvasTop;
    canvasConfig.canvasLeft = config.canvasLeft;
    canvasConfig.canvasBottom = canvasConfig.canvasTop + canvasConfig.canvasHeight;
    canvasConfig.canvasWidth = config.canvasWidth;
    canvasConfig.canvasRight = config.canvasRight;
    canvasConfig.canvasY = config.canvasTop + canvasConfig.canvasHeight + canvasBorderWidth;
    canvas.setDimension({
      top: canvasConfig.canvasTop,
      left: canvasConfig.canvasLeft,
      width: canvasConfig.canvasWidth,
      height: canvasConfig.canvasHeight
    });
    if (showVolumeChart) {
      volumeCanvas = components.canvas[1];
      volumeCanvasConfig = volumeCanvas.config;
      volumeCanvasConfig.canvasHeight = ((volumeHeightPercent / 100) * canvasHeight);
      volumeCanvasConfig.canvasTop = canvasConfig.canvasBottom + xAxisDimensions.bottom +
                (canvasBorderWidth * 2);
      volumeCanvasConfig.canvasLeft = config.canvasLeft;
      volumeCanvasConfig.canvasBottom = volumeCanvasConfig.canvasTop + volumeCanvasConfig.canvasHeight +
                (canvasBorderWidth * 2);
      volumeCanvasConfig.canvasRight = config.canvasRight;
      volumeCanvasConfig.canvasWidth = config.canvasWidth;
      volumeCanvasY = config.canvasTop + canvasConfig.canvasHeight + xAxisDimensions.bottom +
          (canvasBorderWidth * 2);
      volumeCanvasConfig.canvasY = volumeCanvasY;
      volumeCanvas.setDimension({
        top: volumeCanvasConfig.canvasTop,
        left: volumeCanvasConfig.canvasLeft,
        width: volumeCanvasConfig.canvasWidth,
        height: volumeCanvasConfig.canvasHeight
      });
    }
  }
  /**
   * function to calculate post space management calculations
   * calculations like axis dimenetion set, allocate space for xaxis labels,
   * canvas padding etc.
   */
  _postSpaceManagement () {
    super._postSpaceManagement();
  }
  /**
    * Sets dimension of Axis components
    */
  setAxisDimention () {
    let iapi = this,
      components = iapi.getChildren(),
      xAxis = components.xAxis && components.xAxis[0],
      yAxis = components.yAxis && components.yAxis[0],
      vyAxis = components.yAxis && components.yAxis[1],
      canvasArr = components.canvas,
      canvasConfig = canvasArr[0].config,
      volumeCanvasConfig,
      canvasBorderWidth = canvasConfig.canvasBorderWidth,
      axisLeft = canvasConfig.canvasLeft - canvasBorderWidth;
    xAxis && xAxis.setAxisDimention({
      x: canvasConfig.canvasLeft,
      y: xAxis.config.isOpposit
        ? canvasConfig.canvasTop - canvasBorderWidth
        : canvasConfig.canvasY,
      opposite: xAxis.config.isOpposit
        ? canvasConfig.canvasY
        : canvasConfig.canvasTop - canvasBorderWidth,
      axisLength: canvasConfig.canvasWidth
    });
    yAxis && yAxis.setAxisDimention({
      x: yAxis.config.isOpposit
        ? canvasConfig.canvasRight + canvasBorderWidth : axisLeft,
      y: canvasConfig.canvasTop,
      opposite: yAxis.config.isOpposit
        ? axisLeft : canvasConfig.canvasRight + canvasBorderWidth,
      axisLength: canvasConfig.canvasHeight
    });
    xAxis.setCanvas(canvasConfig);
    yAxis.setCanvas(canvasConfig);
    if (iapi.config.showVolumeChart) {
      volumeCanvasConfig = canvasArr[1].config;
      vyAxis && vyAxis.setAxisDimention({
        x: yAxis.config.isOpposit
          ? volumeCanvasConfig.canvasRight + canvasBorderWidth : axisLeft,
        y: volumeCanvasConfig.canvasTop,
        opposite: yAxis.config.isOpposit
          ? axisLeft : volumeCanvasConfig.canvasRight + canvasBorderWidth,
        axisLength: volumeCanvasConfig.canvasHeight
      });
      vyAxis && vyAxis.setCanvas(volumeCanvasConfig);
    }
  }
  /**
   * function to parse JSON data for configurations of axis
   * @return { Object } contains axis configuration
   */
  _feedAxesRawData () {
    var iapi = this,
      colorM = iapi.getFromEnv('color-manager'),
      dataObj = iapi.getFromEnv('dataSource'),
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      xAxisConf,
      yAxisConf,
      vYAxisConf,
      yAxisConfigure = [],
      xAxisConfigure = [],
      is3D = iapi.config.is3D,
      palleteString = is3D ? chartPaletteStr.chart3D : chartPaletteStr.chart2D,
      outCanfontSizeWithUnit = pluckFontSizeMaintainUnit(chartAttrs.outcnvbasefontsize, chartAttrs.basefontsize, 10);

    xAxisConf = {
      isVertical: false,
      isReverse: false,
      isOpposit: isEqualString(chartAttrs.xaxisposition, 'top'),
      drawTrendLabels: true,
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#?([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.xaxisnamepadding,
      axisValuePadding: chartAttrs.labelpadding,
      axisNameFont: chartAttrs.xaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.xaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.xaxisnamefontsize,
      axisNameFontColor: chartAttrs.xaxisnamefontcolor,
      axisNameFontBold: chartAttrs.xaxisnamefontbold,
      axisNameFontItalic: chartAttrs.xaxisnamefontitalic,
      axisNameBgColor: chartAttrs.xaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.xaxisnamebordercolor,
      axisNameAlpha: chartAttrs.xaxisnamealpha,
      axisNameFontAlpha: chartAttrs.xaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.xaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.xaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.xaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.xaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.xaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.xaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.xaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.xaxisnameborderdashgap,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      divLineColor: pluck(chartAttrs.vdivlinecolor, chartAttrs.divlinecolor,
        colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.vdivlinealpha, chartAttrs.divlinealpha,
        is3D ? colorM.getColor(divLineAlpha3DStr) : colorM.getColor(divLineAlphaStr)),
      divLineThickness: pluckNumber(chartAttrs.vdivlinethickness, chartAttrs.divlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.vdivlinedashed, chartAttrs.vdivlineisdashed,
        chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.vdivlinedashlen, chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.vdivlinedashgap, chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatevgridcolor, 0),
      alternateGridColor: pluck(chartAttrs.alternatevgridcolor, colorM.getColor(altVGridColorStr)),
      alternateGridAlpha: pluck(chartAttrs.alternatevgridalpha, colorM.getColor(altVGridAlphaStr)),
      numDivLines: chartAttrs.numvdivlines,
      labelFont: chartAttrs.labelfont,
      labelFontSize: this.computeFontSize(chartAttrs.labelfontsize),
      labelFontSizeWithUnit: chartAttrs.labelfontsize,
      labelFontColor: chartAttrs.labelfontcolor,
      labelAlpha: chartAttrs.labelalpha,
      labelFontBold: chartAttrs.labelfontbold,
      labelFontItalic: chartAttrs.labelfontitalic,
      labelBdrColor: chartAttrs.labelbordercolor,
      labelBdrAlpha: chartAttrs.labelborderalpha,
      labelLink: chartAttrs.labellink,
      labelBgAlpha: chartAttrs.labelbgalpha,
      labelBgColor: chartAttrs.labelbgcolor,
      labelBdrThickness: chartAttrs.labelborderthickness,
      labelBdrPadding: chartAttrs.labelborderpadding,
      labelBdrRadius: chartAttrs.labelborderradius,
      labelBdrDashed: chartAttrs.labelborderdashed,
      labelBdrDashLen: chartAttrs.labelborderdashlen,
      labelBdrDashGap: chartAttrs.labelborderdashgap,
      axisName: chartAttrs.xaxisname,
      axisMinValue: chartAttrs.xaxisminvalue,
      axisMaxValue: chartAttrs.xaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptivexmin,
      adjustDiv: chartAttrs.adjustvdiv,
      labelDisplay: chartAttrs.labeldisplay,
      showLabels: chartAttrs.showlabels,
      rotateLabels: chartAttrs.rotatelabels,
      slantLabel: pluckNumber(chartAttrs.slantlabels, chartAttrs.slantlabel),
      labelStep: pluckNumber(chartAttrs.labelstep, chartAttrs.xaxisvaluesstep),
      showAxisValues: pluckNumber(chartAttrs.showxaxisvalues, chartAttrs.showxaxisvalue),
      showLimits: chartAttrs.showvlimits,
      showDivLineValues: pluckNumber(chartAttrs.showvdivlinevalues, chartAttrs.showvdivlinevalues),
      showZeroPlane: chartAttrs.showvzeroplane,
      zeroPlaneColor: chartAttrs.vzeroplanecolor,
      zeroPlaneThickness: chartAttrs.vzeroplanethickness,
      zeroPlaneAlpha: chartAttrs.vzeroplanealpha,
      showZeroPlaneValue: chartAttrs.showvzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showxaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.xaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.xaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.xaxislinecolor, chartAttrs.axislinecolor, COLOR_000000),
      freezeLimit: true
    };
    xAxisConf.vtrendlines = dataObj.vtrendlines;
    xAxisConfigure.push(xAxisConf);
    yAxisConf = {
      isVertical: true,
      isReverse: true,
      isOpposit: isEqualString(chartAttrs.yaxisposition, 'left'),
      drawLabelsOpposit: 1,
      axisNameAlignCanvas: 1,
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#?([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.yaxisnamepadding,
      axisValuePadding: chartAttrs.yaxisvaluespadding,
      axisNameFont: chartAttrs.pyaxisnamefont,
      axisNameFontSize: chartAttrs.pyaxisnamefontsize,
      axisNameFontSizeWithUnit: chartAttrs.pyaxisnamefontsize,
      axisNameFontColor: chartAttrs.pyaxisnamefontcolor,
      axisNameFontBold: chartAttrs.pyaxisnamefontbold,
      axisNameFontItalic: chartAttrs.pyaxisnamefontitalic,
      axisNameBgColor: chartAttrs.pyaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.pyaxisnamebordercolor,
      axisNameAlpha: chartAttrs.pyaxisnamealpha,
      axisNameFontAlpha: chartAttrs.pyaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.pyaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.pyaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.pyaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.pyaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.pyaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.pyaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.pyaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.pyaxisnameborderdashgap,
      axisNameWidth: chartAttrs.yaxisnamewidth,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      rotateAxisName: pluckNumber(chartAttrs.rotateyaxisname, 1),
      axisName: chartAttrs.pyaxisname,
      divLineColor: pluck(chartAttrs.divlinecolor, colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.divlinealpha, colorM.getColor(divLineAlphaStr)),
      divLineThickness: pluckNumber(chartAttrs.divlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 1)),
      divLineDashLen: pluckNumber(chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatehgridcolor, 1),
      alternateGridColor: pluck(chartAttrs.alternatehgridcolor, colorM.getColor(altHGridColorStr)),
      alternateGridAlpha: pluck(chartAttrs.alternatehgridalpha, colorM.getColor(altHGridAlphaStr)),
      numDivLines: pluckNumber(chartAttrs.numpdivlines, 5),
      axisMinValue: chartAttrs.pyaxisminvalue,
      axisMaxValue: chartAttrs.pyaxismaxvalue,
      setAdaptiveMin: pluckNumber(chartAttrs.setadaptiveymin, 1),
      adjustDiv: chartAttrs.adjustdiv,
      labelStep: chartAttrs.yaxisvaluesstep,
      showAxisValues: pluckNumber(chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue),
      showLimits: pluckNumber(chartAttrs.showyaxislimits, chartAttrs.showlimits, iapi.showLimits),
      showDivLineValues: pluckNumber(chartAttrs.showdivlinevalues, chartAttrs.showdivlinevalue),
      showZeroPlane: chartAttrs.showzeroplane,
      zeroPlaneColor: chartAttrs.zeroplanecolor,
      zeroPlaneThickness: chartAttrs.zeroplanethickness,
      zeroPlaneAlpha: chartAttrs.zeroplanealpha,
      showZeroPlaneValue: chartAttrs.showzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showyaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.yaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.yaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.yaxislinecolor, chartAttrs.axislinecolor, COLOR_000000),
      labelFont: chartAttrs.yaxisvaluefont,
      labelFontSize: this.computeFontSize(chartAttrs.yaxisvaluefontsize),
      labelFontSizeWithUnit: chartAttrs.yaxisvaluefontsize,
      labelFontColor: chartAttrs.yaxisvaluefontcolor,
      labelAlpha: chartAttrs.yaxisvaluealpha,
      labelFontBold: chartAttrs.yaxisvaluefontbold,
      labelFontItalic: chartAttrs.yaxisvaluefontitalic,
      labelBdrColor: chartAttrs.yaxisvaluebordercolor,
      labelBgAlpha: chartAttrs.yaxisvaluebgalpha,
      labelBdrAlpha: chartAttrs.yaxisvalueborderalpha,
      labelLink: chartAttrs.yaxisvaluelink,
      labelBgColor: chartAttrs.yaxisvaluebgcolor,
      labelBdrThickness: chartAttrs.yaxisvalueborderthickness,
      labelBdrPadding: chartAttrs.yaxisvalueborderpadding,
      labelBdrRadius: chartAttrs.yaxisvalueborderradius,
      labelBdrDashed: chartAttrs.yaxisvalueborderdashed,
      labelBdrDashLen: chartAttrs.yaxisvalueborderdashlen,
      labelBdrDashGap: chartAttrs.yaxisvalueborderdashgap
    };
    yAxisConf.trendlines = dataObj.trendlines;
    yAxisConfigure.push(yAxisConf);
    if (iapi.config.showVolumeChart) {
      vYAxisConf = {
        isVertical: true,
        isReverse: true,
        isOpposit: isEqualString(chartAttrs.yaxisposition, 'left'),
        axisIndex: 1,
        drawLabelsOpposit: 1,
        axisNameAlignCanvas: 1,
        uniqueClassName: 1,
        outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
        outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
        outCanfontSizeWithUnit: outCanfontSizeWithUnit,
        outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
          colorM.getColor(palleteString.baseFontColor)).replace(/^#?([a-f0-9]+)/ig, '#$1'),
        axisNamePadding: chartAttrs.yaxisnamepadding,
        axisValuePadding: chartAttrs.yaxisvaluespadding,
        axisNameFont: chartAttrs.vyaxisnamefont,
        axisNameFontSize: chartAttrs.vyaxisnamefontsize,
        axisNameFontSizeWithUnit: chartAttrs.vyaxisnamefontsize,
        axisNameFontColor: chartAttrs.vyaxisnamefontcolor,
        axisNameFontBold: chartAttrs.vyaxisnamefontbold,
        axisNameFontItalic: chartAttrs.vyaxisnamefontitalic,
        axisNameBgColor: chartAttrs.vyaxisnamebgcolor,
        axisNameBorderColor: chartAttrs.vyaxisnamebordercolor,
        axisNameAlpha: chartAttrs.vyaxisnamealpha,
        axisNameFontAlpha: chartAttrs.vyaxisnamefontalpha,
        axisNameBgAlpha: chartAttrs.vyaxisnamebgalpha,
        axisNameBorderAlpha: chartAttrs.vyaxisnameborderalpha,
        axisNameBorderPadding: chartAttrs.vyaxisnameborderpadding,
        axisNameBorderRadius: chartAttrs.vyaxisnameborderradius,
        axisNameBorderThickness: chartAttrs.vyaxisnameborderthickness,
        axisNameBorderDashed: chartAttrs.vyaxisnameborderdashed,
        axisNameBorderDashLen: chartAttrs.vyaxisnameborderdashlen,
        axisNameBorderDashGap: chartAttrs.vyaxisnameborderdashgap,
        axisNameWidth: chartAttrs.yaxisnamewidth,
        useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
        rotateAxisName: pluckNumber(chartAttrs.rotateyaxisname, 1),
        axisName: chartAttrs.vyaxisname,
        divLineColor: pluck(chartAttrs.divlinecolor, colorM.getColor(palleteString.divLineColor)),
        divLineAlpha: pluck(chartAttrs.divlinealpha, colorM.getColor(divLineAlphaStr)),
        divLineThickness: pluckNumber(chartAttrs.divlinethickness, 1),
        divLineIsDashed: Boolean(pluckNumber(chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 1)),
        divLineDashLen: pluckNumber(chartAttrs.divlinedashlen, 4),
        divLineDashGap: pluckNumber(chartAttrs.divlinedashgap, 2),
        showAlternateGridColor: pluckNumber(chartAttrs.showalternatehgridcolor, 1),
        alternateGridColor: pluck(chartAttrs.alternatehgridcolor, colorM.getColor(altHGridColorStr)),
        alternateGridAlpha: pluck(chartAttrs.alternatehgridalpha, colorM.getColor(altHGridAlphaStr)),
        numDivLines: chartAttrs.numdivlines,
        // @todo have to change the y axis max and min value afterwards when percentage axis is ready
        axisMinValue: chartAttrs.vyaxisminvalue,
        axisMaxValue: chartAttrs.vyaxismaxvalue,
        setAdaptiveMin: chartAttrs.setadaptiveymin,
        adjustDiv: chartAttrs.adjustdiv,
        labelStep: chartAttrs.yaxisvaluesstep,
        showAxisValues: pluckNumber(chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue),
        showLimits: pluckNumber(chartAttrs.showsecondarylimits, chartAttrs.showlimits),
        showDivLineValues: pluckNumber(chartAttrs.showdivlinevalues, chartAttrs.showdivlinevalue),
        showZeroPlane: chartAttrs.showzeroplane,
        zeroPlaneColor: chartAttrs.zeroplanecolor,
        zeroPlaneThickness: chartAttrs.zeroplanethickness,
        zeroPlaneAlpha: chartAttrs.zeroplanealpha,
        showZeroPlaneValue: chartAttrs.showzeroplanevalue,
        trendlineColor: chartAttrs.trendlinecolor,
        trendlineToolText: chartAttrs.trendlinetooltext,
        trendlineThickness: chartAttrs.trendlinethickness,
        trendlineAlpha: chartAttrs.trendlinealpha,
        showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
        showAxisLine: pluckNumber(chartAttrs.showyaxisline, chartAttrs.showaxislines,
          chartAttrs.drawAxisLines, 0),
        axisLineThickness: pluckNumber(chartAttrs.yaxislinethickness, chartAttrs.axislinethickness, 1),
        axisLineAlpha: pluckNumber(chartAttrs.yaxislinealpha, chartAttrs.axislinealpha, 100),
        axisLineColor: pluck(chartAttrs.yaxislinecolor, chartAttrs.axislinecolor, COLOR_000000),
        labelFont: chartAttrs.yaxisvaluefont,
        labelFontSize: this.computeFontSize(chartAttrs.yaxisvaluefontsize),
        labelFontSizeWithUnit: chartAttrs.yaxisvaluefontsize,
        labelFontColor: chartAttrs.yaxisvaluefontcolor,
        labelAlpha: chartAttrs.yaxisvaluealpha,
        labelFontBold: chartAttrs.yaxisvaluefontbold,
        labelFontItalic: chartAttrs.yaxisvaluefontitalic,
        labelBdrColor: chartAttrs.yaxisvaluebordercolor,
        labelBgAlpha: chartAttrs.yaxisvaluebgalpha,
        labelBdrAlpha: chartAttrs.yaxisvalueborderalpha,
        labelLink: chartAttrs.yaxisvaluelink,
        labelBgColor: chartAttrs.yaxisvaluebgcolor,
        labelBdrThickness: chartAttrs.yaxisvalueborderthickness,
        labelBdrPadding: chartAttrs.yaxisvalueborderpadding,
        labelBdrRadius: chartAttrs.yaxisvalueborderradius,
        labelBdrDashed: chartAttrs.yaxisvalueborderdashed,
        labelBdrDashLen: chartAttrs.yaxisvalueborderdashlen,
        labelBdrDashGap: chartAttrs.yaxisvalueborderdashgap
      };
      yAxisConfigure.push(vYAxisConf);
    }
    return { xAxisConfigure, yAxisConfigure };
  }
  mouseoutHandler (e, _lastDatasetIndex, _lastPointIndex) {
    let chart = this,
      datasets = chart.config.datasetOrder || chart.getDatasets(),
      mouseTracker = chart.getChildren('mouseTracker')[0];
    // check if the data plot exists. else hide all existing tooltips.
    if (datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex].components.data[_lastPointIndex]) {
      datasets[_lastDatasetIndex]._firePlotEvent(MOUSEOUT, _lastPointIndex, e);
    } else {
      chart.getFromEnv('toolTipController').hideAll();
    }
    // delete stored last ds details
    delete mouseTracker._lastDatasetIndex;
    delete mouseTracker._lastPointIndex;
  }
  /**
   * This method delegates the handling of  mouse event to the chart's event handler
   * @param  {Object} e The Event Object
   * @param  {Object} data The Data Object
   * @private
   */
  _mouseEvtHandler (e, data) {
    var iapi = this,
      mouseTracker = data.mouseTracker,
      oriEvent = e.originalEvent,
      canvasLen = iapi.getChildren('canvas').length, // eslint-disable-line good-practices/no-single-usage-variable
      datasets,
      coordinate = oriEvent && getMouseCoordinate(iapi.getFromEnv('chart-container'), oriEvent, iapi),
      chartX = (coordinate && coordinate.chartX) || 0, // eslint-disable-line good-practices/no-single-usage-variable
      chartY = (coordinate && coordinate.chartY) || 0, // eslint-disable-line good-practices/no-single-usage-variable
      dataset,
      hoveredInfo,
      pointFound = false,
      i,
      j,
      l,
      derivedEvensInfo,
      _lastDatasetIndex = mouseTracker._lastDatasetIndex,
      _lastPointIndex = mouseTracker._lastPointIndex;
    for (i = 0; i < canvasLen; i++) {
      // todo make sure the datasets are as per their z-order
      datasets = iapi.getDatasets();
      j = datasets.length;
      while (j-- && !pointFound) {
        dataset = datasets[j];
        if (dataset && dataset.getState('visible')) {
          hoveredInfo = dataset._getHoveredPlot && dataset._getHoveredPlot(chartX, chartY);
          if (hoveredInfo && hoveredInfo.hovered) {
            pointFound = true;
            hoveredInfo.datasetIndex = j;
            derivedEvensInfo = mouseTracker.getMouseEvents(e, hoveredInfo.datasetIndex, hoveredInfo.pointIndex);
          }
        }
      }
    }

    // todo instead of sending event names, create a event object of that type and send it

    // fire out on last hovered plot
    if ((!pointFound || (derivedEvensInfo && derivedEvensInfo.fireOut)) && typeof _lastDatasetIndex !== 'undefined') {
      if (datasets[_lastDatasetIndex] && datasets[_lastDatasetIndex]._firePlotEvent) {
        // when mouseout is fired and there aren't any events that need to be fired over the current plot,
        // call mouseouthamndler through settimeout.
        if (derivedEvensInfo && !derivedEvensInfo.events.length) {
          mouseTracker.mouseoutTimer = setTimeout(function () {
            iapi.mouseoutHandler(e, _lastDatasetIndex, _lastPointIndex);
          }, 20);
        } else {
          iapi.mouseoutHandler(e, _lastDatasetIndex, _lastPointIndex);
          clearTimeout(mouseTracker.mouseoutTimer);
        }
      }

      // todo scope to have sticky tracked tooltip
    }
    // fire remaining events
    if (pointFound) {
      l = derivedEvensInfo.events && derivedEvensInfo.events.length;
      // Update only when events for current plot are present.
      if (l) {
      // store the index of the hovered DS and plot
        mouseTracker._lastDatasetIndex = hoveredInfo.datasetIndex;
        _lastPointIndex = mouseTracker._lastPointIndex = hoveredInfo.pointIndex;
      }
      for (j = 0; j < l; j += 1) {
        dataset && dataset._firePlotEvent && dataset._firePlotEvent(derivedEvensInfo.events[j],
          _lastPointIndex, e, hoveredInfo.datasetIndex);
      }
    }
  }
  /**
   * allocate space for canvas
   * @param  {Object} dimensions contains left, right, top, bottom co-ordinate (relative)
   */
  _allocateSpace (dimensions) {
    var iapi = this,
      canvas = iapi.getChildren('canvas'),
      canvasConfig = canvas && canvas[0].config,
      config = iapi.config,
      canvasHeight = config.canvasHeight,
      canvasWidth = config.canvasWidth,
      canvasTop,
      canvasLeft;

    canvasLeft = config.canvasLeft += dimensions.left || 0; // eslint-disable-line good-practices/no-single-usage-variable
    canvasTop = config.canvasTop += dimensions.top || 0; // eslint-disable-line good-practices/no-single-usage-variable
    canvasWidth = config.canvasWidth =
              mathMax(canvasWidth - ((dimensions.left || 0) + (dimensions.right || 0)), 0);
    canvasHeight = config.canvasHeight =
              mathMax(canvasHeight - ((dimensions.top || 0) + (dimensions.bottom || 0)), 0);

    config.availableHeight = mathMax((config.availableHeight - ((dimensions.top || 0) + (dimensions.bottom || 0))),
      0);
    config.availableWidth = mathMax((config.availableWidth - ((dimensions.left || 0) + (dimensions.right || 0))), 0);
    config.canvasRight = canvasLeft + canvasWidth;
    config.canvasBottom = canvasTop + canvasHeight;

    if (canvasConfig) {
      canvasConfig.canvasPaddingLeft = mathMax(canvasConfig.canvasPaddingLeft, dimensions.paddingLeft || 0);
      canvasConfig.canvasPaddingRight = mathMax(canvasConfig.canvasPaddingRight, dimensions.paddingRight || 0);
      canvasConfig.canvasPaddingTop = mathMax(canvasConfig.canvasPaddingTop, dimensions.paddingTop || 0);
      canvasConfig.canvasPaddingBottom = mathMax(canvasConfig.canvasPaddingBottom, dimensions.paddingBottom || 0);
    }
  }

  /**
   * function to check if the chart specific data is proper is not
   * this fn is define for specific chart types
   * @return {boolean} if JSON data is valid or not
   */
  _checkInvalidSpecificData () {
    let datasetsJSON = this.getFromEnv('dataSource').dataset,
      data = datasetsJSON && datasetsJSON[0] && datasetsJSON[0].data;
    if (!datasetsJSON || !data || !Array.isArray(data)) {
      return true;
    }
  }
}
export default CandleStick;
